import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { NotificationProvider } from './context/NotificationContext';
import { IntelligenceJobsProvider } from './context/IntelligenceJobsContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { msalConfig } from './auth/msalConfig';
import NavbarMUI from './components/NavbarMUI';
import Login from './pages/Login';

const DashboardMUI = lazy(() => import('./pages/DashboardMUI'));
const AccountListMUI = lazy(() => import('./pages/AccountListMUI'));
const QueuesMUI = lazy(() => import('./pages/QueuesMUI'));
const AlertsMUI = lazy(() => import('./pages/AlertsMUI'));
const SyncHistoryMUI = lazy(() => import('./pages/SyncHistoryMUI'));
const SyncDetailMUI = lazy(() => import('./pages/SyncDetailMUI'));
const SyncPeriodDetailMUI = lazy(() => import('./pages/SyncPeriodDetailMUI'));
const SettingsMUI = lazy(() => import('./pages/SettingsMUI'));

const CURRENT_VIEW_STORAGE_KEY = 'atlas_current_view';
const HEADER_COLLAPSED_KEY = 'atlas_header_collapsed';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
msalInstance.initialize().catch(console.error);

/**
 * Loading Screen Component
 */
function LoadingScreen() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        bgcolor: 'background.default'
      }}
    >
      <CircularProgress size={60} sx={{ mb: 3 }} />
      <Typography variant="h6" color="text.secondary">
        Loading ATLAS...
      </Typography>
    </Box>
  );
}

function RouteFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minHeight: 320,
        bgcolor: 'background.default'
      }}
    >
      <CircularProgress size={36} />
    </Box>
  );
}

/**
 * Main App Content (after authentication)
 */
function AppContent() {
  const { isAuthenticated, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current view from path
  const getCurrentViewFromPath = useCallback(() => {
    const path = location.pathname;
    if (path.startsWith('/sync/')) return 'sync-history';
    if (path === '/sync-history') return 'sync-history';
    if (path === '/accounts') return 'accounts';
    if (path === '/queues' || path === '/findings') return 'findings';
    if (path === '/alerts') return 'alerts';
    if (path === '/settings' || path === '/users') return 'settings';
    if (path === '/high-risk') return 'high-risk';
    return 'dashboard';
  }, [location.pathname]);

  // Path is always the source of truth for currentView (deep links must win over localStorage)
  const [currentView, setCurrentView] = useState(() => getCurrentViewFromPath());

  // Header collapsed state
  const [headerCollapsed, setHeaderCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(HEADER_COLLAPSED_KEY);
      return saved === 'true';
    } catch (error) {
      return false;
    }
  });

  // Toggle header collapsed state
  const toggleHeader = useCallback(() => {
    setHeaderCollapsed(prev => {
      const newValue = !prev;
      try {
        localStorage.setItem(HEADER_COLLAPSED_KEY, String(newValue));
      } catch (error) {
        console.error('Failed to save header state:', error);
      }
      return newValue;
    });
  }, []);

  // Handle navigation with both view state and URL
  const handleNavigate = useCallback((view) => {
    setCurrentView(view);
    const routes = {
      'dashboard': '/',
      'accounts': '/accounts',
      'findings': '/findings',
      'queues': '/findings',
      'high-risk': '/high-risk',
      'alerts': '/alerts',
      'settings': '/settings',
      'sync-history': '/sync-history'
    };
    navigate(routes[view] || '/');
  }, [navigate]);

  // Sync view with URL changes
  useEffect(() => {
    const pathView = getCurrentViewFromPath();
    if (pathView !== currentView && !location.pathname.startsWith('/sync/')) {
      setCurrentView(pathView);
    }
  }, [location.pathname, getCurrentViewFromPath]);

  // Keyboard shortcut to toggle header (press 'h' or Escape)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      
      // Press 'h' to toggle header
      if (e.key === 'h' || e.key === 'H') {
        toggleHeader();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleHeader]);

  // Save current view to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CURRENT_VIEW_STORAGE_KEY, currentView);
    } catch (error) {
      console.error('Failed to save current view to localStorage:', error);
    }
  }, [currentView]);

  // Redirect from protected pages if user doesn't have access
  useEffect(() => {
    if (currentView === 'settings' && !isAdmin && !loading) {
      handleNavigate('dashboard');
    }
  }, [currentView, isAdmin, loading, handleNavigate]);

  // Show loading screen while checking auth
  if (loading) {
    return <LoadingScreen />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <NavbarMUI 
        currentView={currentView} 
        onNavigate={handleNavigate} 
        collapsed={headerCollapsed}
        onToggleCollapse={toggleHeader}
      />
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<DashboardMUI headerCollapsed={headerCollapsed} />} />
            <Route path="/accounts" element={<AccountListMUI headerCollapsed={headerCollapsed} />} />
            <Route path="/findings" element={<QueuesMUI />} />
            <Route path="/queues" element={<Navigate to="/findings" replace />} />
            <Route path="/review" element={<Navigate to="/" />} />
            <Route path="/high-risk" element={<AccountListMUI headerCollapsed={headerCollapsed} />} />
            <Route path="/alerts" element={<AlertsMUI headerCollapsed={headerCollapsed} />} />
            <Route path="/settings" element={
              isAdmin ? <SettingsMUI /> : <Navigate to="/" />
            } />
            <Route path="/users" element={<Navigate to="/settings?tab=users" replace />} />
            <Route path="/sync-history" element={<SyncHistoryMUI />} />
            <Route path="/sync/period" element={<SyncPeriodDetailMUI />} />
            <Route path="/sync/:jobId" element={<SyncDetailMUI />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </Box>
    </Box>
  );
}

/**
 * Root App Component
 */
function App() {
  return (
    <BrowserRouter>
      <MsalProvider instance={msalInstance}>
        <AuthProvider>
          <NotificationProvider>
            <IntelligenceJobsProvider>
              <AppContent />
            </IntelligenceJobsProvider>
          </NotificationProvider>
        </AuthProvider>
      </MsalProvider>
    </BrowserRouter>
  );
}

export default App;
